import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { generatePrompt } from "../services/apiService"; // Adjust the import path as necessary

const ImagePromptGenerator = ({ open, onClose, initialText = "" }) => {
  const [model, setModel] = useState("Stable Diffusion");
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  // Set initial text whenever the dialog opens or initialText changes
  useEffect(() => {
    if (open && initialText) {
      setPrompt(initialText);
    }
  }, [open, initialText]);

  const handleModelChange = (event) => {
    setModel(event.target.value);
  };

  const handlePromptChange = (event) => {
    setPrompt(event.target.value);
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError("");
    setGeneratedPrompt("");

    try {
      // Call the generate_prompt endpoint
      let payload = {
        model_name: "gpt4-0",
        input_phrase: prompt,
      };

      const response = await generatePrompt(payload);
      if (response.image_prompt) {
        // Extract and display the image prompt on success
        const formattedText = response.image_prompt
          .replace(/^\s*### (.*)$/gm, "<b>$1</b>")
          .replace(/^\s*## (.*)$/gm, "<b>$1</b>")
          .replace(/^\s*# (.*)$/gm, "<b>$1</b>")
          .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
          // .replace(/^[-*•] (.*)$/gm, "<ul><li>$1</li></ul>")
          .replace(/\n/g, "<br>");
        // .replace(/\n/g, "<br/>")
        // .replace(/^### (.*$)/gim, "<h1>$1</h1>")
        // .replace(/^## (.*$)/gim, "<h2>$1</h1>")
        // .replace(/^# (.*$)/gim, "<h1>$1</h1>");
        setGeneratedPrompt(formattedText);
      } else {
        setError("Failed to generate prompt. Please try again.");
      }
    } catch (error) {
      setError("Failed to generate prompt. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle copy to clipboard with notification
  const handleCopyToClipboard = () => {
    // Create a temporary element to strip HTML tags
    const tempElement = document.createElement("div");
    tempElement.innerHTML = generatedPrompt;
    const textToCopy = tempElement.innerText || tempElement.textContent;

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => {
          setCopySuccess(false);
        }, 3000); // Hide notification after 3 seconds
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
      });
  };

  // Close the notification
  const handleCloseCopyNotification = () => {
    setCopySuccess(false);
  };

  const closePopup = () => {
    setError("");
    setPrompt("");
    setGeneratedPrompt("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={closePopup}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "10px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" component="div">
          Generate Image Prompt
        </Typography>
        <IconButton
          aria-label="close"
          onClose={closePopup}
          onClick={closePopup}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ mt: 1, padding: "0 24px 5px" }}>
        <Box sx={{ mb: 3, mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography sx={{ mr: 1, minWidth: "100px" }}>AI Model:</Typography>
            <FormControl fullWidth>
              <Select
                value={model}
                onChange={handleModelChange}
                displayEmpty
                sx={{
                  borderRadius: "8px",
                  height: "48px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                }}
                disabled="true"
              >
                <MenuItem value="Stable Diffusion">Stable Diffusion</MenuItem>
                <MenuItem value="Flux Dev">Flux Dev</MenuItem>
                <MenuItem value="DALL-E">DALL-E</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="The image generation module is currently in progress. In the meantime, you can generate a prompt for Copilot from the platform and use to create the image.">
              <IconButton>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
            <Typography sx={{ mb: 1 }}>Enter your image request:</Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={prompt}
              onChange={handlePromptChange}
              placeholder="e.g., @image mustard-honey flavour for **Goldfish**"
              sx={{
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                },
              }}
              autoFocus
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: "0 24px 16px",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Button
          onClick={handleGenerate}
          variant="contained"
          fullWidth
          disabled={isGenerating || !prompt.trim()}
          sx={{
            borderRadius: "8px",
            padding: "12px",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: "500",
            background: "linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)",
            "&:hover": {
              background: "linear-gradient(90deg, #5253d4 0%, #7949e0 100%)",
            },
            "&:disabled": {
              background: "#e0e0e0",
              color: "#a0a0a0",
            },
          }}
        >
          {isGenerating ? "Generating..." : "Generate Image Prompt"}
        </Button>

        {/* Show error message if there is one */}
        {error && (
          <Typography
            color="error"
            sx={{
              width: "100%",
              mb: 2,
              mt: 2,
              p: 2,
              borderRadius: "4px",
              bgcolor: "#ffebee",
              fontSize: "14px",
            }}
          >
            {error}
          </Typography>
        )}

        {/* Show generated prompt if available */}
        {generatedPrompt && (
          <Box sx={{ width: "100%", mb: 3, mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Generated Image Prompt:
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: "4px",
                bgcolor: "#f0f7ff",
                border: "1px solid #bbdefb",
                fontSize: "14px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: "200px",
                overflow: "auto",
              }}
            >
              <Typography
                // style={{
                // //   fontFamily: "'Poppins', sans-serif",
                //   fontSize: "14px",
                //   color: "rgba(0, 0, 0, 0.7)",
                // }}
                dangerouslySetInnerHTML={{ __html: generatedPrompt }}
              ></Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Tooltip title="Copy to clipboard">
                <IconButton onClick={handleCopyToClipboard}>
                  <ContentCopyIcon size="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </DialogActions>

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleCloseCopyNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseCopyNotification}
          severity="success"
          sx={{ width: "100%" }}
        >
          Prompt copied to clipboard!
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

ImagePromptGenerator.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  initialText: PropTypes.string
};



export default ImagePromptGenerator;
